/**
 * The company-level detail behind the five-year placement record's category
 * mix (COMPANY_TYPES in placementTrends.ts), from the Training & Placement
 * Cell's own "Placement & Industry Engagement Report" deck — the same 639 /
 * 90% / 180+ / 40 LPA headline figures that appear elsewhere on this site.
 *
 * Read from the deck's own five "Recruiter Directory" slides and reproduced
 * under the deck's own category headings, in the deck's own order. Two things
 * carried rather than corrected:
 *
 *   1. The deck's first category, "Product-Based / IT Product / IT Services /
 *      Consulting", bundles what COMPANY_TYPES counts as two separate
 *      categories (IT Product: 19, IT Service & Consulting: 58, sum 77). This
 *      list holds 60 names under that bundled heading — short of 77, and not
 *      reconcilable from the deck alone (there is no per-company split telling
 *      a name apart as "product" vs "service"). The two are different views of
 *      the same recruiter base, not contradictory counts; neither is
 *      corrected to match the other.
 *   2. "R&D / Niche Engineering" holds 14 names here against 15 in
 *      COMPANY_TYPES. One company from that category is present in the
 *      Cell's total but not legible in the source deck.
 *
 * The other four categories (Core Engineering / Manufacturing 40, Apparel &
 * Textile Export 13, Pharma / Biotech / Chemical 11, Internship & Skilling
 * Programs 32) match COMPANY_TYPES exactly.
 */

export interface RecruiterDirectoryEntry {
  name: string;
  category: string;
}

/** In the deck's own order. */
export const RECRUITER_CATEGORIES = [
  "Product-Based / IT Product / IT Services / Consulting",
  "Core Engineering / Manufacturing",
  "R&D / Niche Engineering",
  "Apparel & Textile Export",
  "Pharma / Biotech / Chemical",
  "Internship & Skilling Programs",
] as const;

export type RecruiterCategory = (typeof RECRUITER_CATEGORIES)[number];

const IT: string[] = [
  "Tata Consultancy Services (Priority Institute)", "Cognizant", "MuSigma", "IBM", "Infosys", "Deloitte", "KPMG",
  "Cleo Software", "Accenture", "Toshiba", "Capgemini", "Dish Network", "EY", "Hashedin Technology",
  "LTI Mindtree", "iNube Software Solutions", "Wipro", "Nomiso", "Sasken", "Protech (Japan Based Company)",
  "Redbus", "FAI", "Xoriant", "Rupeek", "Amadeus Labs", "Gigsky", "Ingest Labs",
  "Logic brackets", "Odyessus Solution", "Maple Labs", "Altimetrix", "Rakuten", "Ampcus Cyber India", "Kaynes Technology",
  "Zscaler", "Radisys", "Coditas", "Kasmo Tech", "Tech Mahindra", "Zepto", "Western Digital",
  "SLK Software", "Akamai", "Alt Digital", "Magic Pixel", "Quinbay Tech", "CloudifyOps", "TVS-SCS",
  "iVoyant", "Sonata Software", "RAS Technology", "SANRadiance Technologies", "Skyscend", "Tech Fortune", "Zensar",
  "Robosoft Technologies", "ICSBIT LABS", "Work Force Software", "Tayana Software", "Someshawara Software",
];

const CORE_ENGINEERING: string[] = [
  "Aarbee Structures", "Accord", "Ace Designers", "Advance Mechanical Services", "ASC International", "Aradya Steel", "AT&S",
  "Atkins", "Aum Water Technologies", "Autoliv India", "Baldota Group", "Blanco Steel Detailing", "Cargill India", "Emmvee Energy",
  "Graintec", "Grasim Industries", "HERIN ELECTRONICS", "Indo-MIM", "Indus Towers", "JSW", "Kalpataru Projects Intl.",
  "Key Sustainability Solutions", "Kirloskar Ferrous", "LM Wind Power", "Lucas TVS", "Mahanagara Gas Ltd.", "Maini Precision Products", "MOTHERSON",
  "Middleby", "Nirvoday Pvt Ltd", "PRDC", "Pragathi Polymers", "Pragathi / Prerana Engg Works", "RDC Concrete", "Sansera Engineering",
  "Schneider Electric", "Sobha", "Suprajit Engineering", "UHP Technologies", "Vajra Incorporation.",
];

const RND: string[] = [
  "Anora Semiconductors", "Bosch Global Services", "Brigosha Technologies", "Delphi-TVS Technologies", "DMG MORI INDIA", "FOXCONN", "Insemi",
  "Maintec Technologies", "Mercedes Benz R&D", "Sasken Technologies", "SKYi Composites", "Tessolve Semiconductors", "Yokogawa", "Zosh Aerospace",
];

const APPAREL: string[] = [
  "Arvind Mill", "Avantara Tailor Mart", "Avery Dennison", "Classic Fashion Apparel", "FirstSteps Babywear", "Gokaldas Exports", "Laguna Clothing",
  "Madhura Garments", "Shahi Exports", "Silver Crest Clothing", "Vishwaa Apparels", "Wonderblues Apparels", "Jockey India",
];

const PHARMA: string[] = [
  "Auro Peptides", "Biotree India", "Cipla", "CuraTeQ Biologics", "Epsilon Carbon", "Manjushree Spn Tech", "Optum",
  "PSK Pharma", "Synthite Industries", "TheraNym Biologics", "Unisur Lifecare",
];

const INTERNSHIP: string[] = [
  "CodeYoung", "Corizo", "Dextris / Echo Brains", "Dhee Coding Lab", "Dyashin DSEdify", "Edu station", "Elytrhra Edufi Tech",
  "Face Prep", "Genz Educate Wing", "Global Quest Tech", "Heartiest Mind Tech", "InternSpot", "Internz Valley", "Intrainz",
  "LearnFlu", "Palle Tech", "Pentagon Space", "Q Spiders", "Rinex Technologies", "SILVER PEAK", "Skill Forge",
  "SkyllX Technologies", "Talent Serve", "TAP Academy", "Teachnook", "Techurate", "Tekworks", "ThinkSync",
  "Vstand4u", "VTech Integrated Sol.", "X-Ciencia", "X-Workz",
];

const byCategory: Array<[RecruiterCategory, string[]]> = [
  [RECRUITER_CATEGORIES[0], IT],
  [RECRUITER_CATEGORIES[1], CORE_ENGINEERING],
  [RECRUITER_CATEGORIES[2], RND],
  [RECRUITER_CATEGORIES[3], APPAREL],
  [RECRUITER_CATEGORIES[4], PHARMA],
  [RECRUITER_CATEGORIES[5], INTERNSHIP],
];

export const RECRUITER_DIRECTORY: RecruiterDirectoryEntry[] = byCategory.flatMap(
  ([category, names]) => names.map((name) => ({ name, category })),
);
